import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  CRMCommunication,
  CRMCompany,
  CRMIndividual,
  CRMOpportunity,
  PipelineStage,
} from "@/types";

// --- Companies ---

export function useCompanies(filters?: { pipeline_stage?: PipelineStage; search?: string }) {
  const params = new URLSearchParams();
  if (filters?.pipeline_stage) params.set("pipeline_stage", filters.pipeline_stage);
  if (filters?.search) params.set("search", filters.search);

  return useQuery<CRMCompany[]>({
    queryKey: ["crm-companies", filters],
    queryFn: async () => {
      const res = await apiClient.get<{ companies: CRMCompany[] }>(
        `/crm/companies?${params.toString()}`,
      );
      return res.data.companies;
    },
  });
}

export function useCompany(id: string | undefined) {
  return useQuery<CRMCompany>({
    queryKey: ["crm-company", id],
    queryFn: async () => {
      const res = await apiClient.get<CRMCompany>(`/crm/companies/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<CRMCompany>("/crm/companies", data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-companies"] }),
  });
}

export function useUpdateCompany(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put<CRMCompany>(`/crm/companies/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-companies"] });
      qc.invalidateQueries({ queryKey: ["crm-company", id] });
    },
  });
}

// --- Meeting scheduling ---

export interface ScheduleMeetingRequest {
  company_id?: string | null;
  individual_id?: string | null;
  opportunity_id?: string | null;
  summary: string;
  starts_at: string; // ISO 8601 with timezone
  ends_at: string;
  agenda?: string | null;
  attendee_emails?: string[];
  location?: string | null;
  include_google_meet?: boolean;
  send_invites?: boolean;
}

export interface ScheduleMeetingResponse {
  followup_id: string;
  calendar_event_id: string | null;
  html_link: string | null;
  hangout_link: string | null;
}

export interface UpcomingMeeting {
  id: string;
  summary: string;
  starts_at: string | null;
  ends_at: string | null;
  attendee_emails: string[];
  calendar_event_id: string | null;
  individual_id: string | null;
  company_id: string | null;
  opportunity_id: string | null;
  status: string;
}

export function useScheduleMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ScheduleMeetingRequest) => {
      const res = await apiClient.post<ScheduleMeetingResponse>("/crm/meetings/schedule", body);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      // The upcoming-meetings panel and the company-scoped comms list are stale.
      if (vars.company_id) {
        qc.invalidateQueries({ queryKey: ["crm-upcoming-meetings", vars.company_id] });
      }
      if (vars.individual_id) {
        qc.invalidateQueries({
          queryKey: ["crm-upcoming-meetings-individual", vars.individual_id],
        });
      }
    },
  });
}

export function useUpcomingMeetingsForCompany(companyId: string | undefined) {
  return useQuery<UpcomingMeeting[]>({
    queryKey: ["crm-upcoming-meetings", companyId],
    queryFn: async () => {
      const res = await apiClient.get<{ meetings: UpcomingMeeting[] }>(
        `/crm/meetings/upcoming?company_id=${companyId}&limit=20`,
      );
      return res.data.meetings;
    },
    enabled: !!companyId,
  });
}

export function usePastUnloggedMeetingsForCompany(companyId: string | undefined) {
  return useQuery<UpcomingMeeting[]>({
    queryKey: ["crm-past-unlogged-meetings", companyId],
    queryFn: async () => {
      const res = await apiClient.get<{ meetings: UpcomingMeeting[] }>(
        `/crm/meetings/past-unlogged?company_id=${companyId}&limit=20`,
      );
      return res.data.meetings;
    },
    enabled: !!companyId,
  });
}

export function useLogMeetingNotes(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      followupId,
      notes,
      direction = "outbound",
    }: {
      followupId: string;
      notes: string;
      direction?: "outbound" | "inbound";
    }) => {
      const res = await apiClient.post<{
        communication_id: string;
        followup_id: string;
        occurred_at: string | null;
      }>(`/crm/followups/${followupId}/log-meeting-notes`, { notes, direction });
      return res.data;
    },
    onSuccess: () => {
      if (companyId) {
        // The past-unlogged list drops this row, the comms list gains a new entry.
        qc.invalidateQueries({ queryKey: ["crm-past-unlogged-meetings", companyId] });
        qc.invalidateQueries({ queryKey: ["crm-comms", companyId] });
      }
    },
  });
}

// --- Gmail comms backfill ---

export interface CommsBackfillResult {
  new: number;
  skipped_dupe: number;
  skipped_unmatched_addr?: number;
  individual_email?: string | null;
  errors: { message?: string; message_id?: string; individual_id?: string }[];
  per_individual?: {
    individual_id: string | null;
    individual_email: string | null;
    new: number;
    skipped_dupe: number;
    errors: { message?: string; message_id?: string }[];
  }[];
}

export function useBackfillCompanyComms(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sinceDays: number) => {
      const res = await apiClient.post<CommsBackfillResult>(
        `/crm/companies/${companyId}/backfill-comms?since_days=${sinceDays}`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-comms", companyId] });
    },
  });
}

export function useBackfillIndividualComms(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      individualId,
      sinceDays = 90,
    }: {
      individualId: string;
      sinceDays?: number;
    }) => {
      const res = await apiClient.post<CommsBackfillResult>(
        `/crm/individuals/${individualId}/backfill-comms?since_days=${sinceDays}`,
      );
      return res.data;
    },
    onSuccess: () => {
      // The comms list is keyed by companyId — invalidate both that and
      // any per-individual query if we add one later.
      if (companyId) qc.invalidateQueries({ queryKey: ["crm-comms", companyId] });
    },
  });
}

// --- Bulk import ---

export interface BulkImportPreviewRow {
  line_number: number;
  first_name: string;
  last_name: string;
  email: string | null;
  company_name: string | null;
  job_title: string | null;
  phone: string | null;
  linkedin_url: string | null;
  notes: string | null;
  company_status: "new" | "existing" | "blank";
  individual_status: "new" | "existing" | "blank";
  existing_company_id: string | null;
  existing_individual_id: string | null;
}

export interface BulkImportPreview {
  parsed_count: number;
  new_companies: string[];
  existing_companies_matched: number;
  new_individuals: number;
  existing_individuals_matched: number;
  errors: { line_number: number; message: string }[];
  rows: BulkImportPreviewRow[];
}

export interface BulkImportResult {
  companies_created: number;
  companies_reused: number;
  individuals_created: number;
  individuals_reused: number;
  individual_ids: string[];
  errors: { line_number: number; message: string }[];
}

export function useBulkImportPreview() {
  return useMutation({
    mutationFn: async (raw: string) => {
      const res = await apiClient.post<BulkImportPreview>("/crm/import/preview", { raw });
      return res.data;
    },
  });
}

export function useBulkImportApply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ raw, source_label }: { raw: string; source_label?: string }) => {
      const res = await apiClient.post<BulkImportResult>("/crm/import/apply", {
        raw,
        source_label: source_label ?? "bulk_import",
      });
      return res.data;
    },
    onSuccess: () => {
      // Both lists are stale after a bulk apply.
      qc.invalidateQueries({ queryKey: ["crm-companies"] });
      qc.invalidateQueries({ queryKey: ["crm-individuals"] });
    },
  });
}

// --- LinkedIn Quick Add (Wave 1) ---

export interface QuickAddLinkedInRequest {
  first_name: string;
  last_name: string;
  linkedin_url?: string | null;
  email?: string | null;
  company_name?: string | null;
  interaction: string;
  conversion_intent: string;
  note?: string | null;
}

export interface QuickAddLinkedInResponse {
  individual_id: string;
  company_id: string | null;
  company_created: boolean;
  individual_created: boolean;
  followups_created: number;
  template: string;
}

export function useQuickAddLinkedIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: QuickAddLinkedInRequest) => {
      const res = await apiClient.post<QuickAddLinkedInResponse>("/crm/quick-add-linkedin", data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-companies"] });
      qc.invalidateQueries({ queryKey: ["crm-individuals"] });
      qc.invalidateQueries({ queryKey: ["followups"] });
    },
  });
}

// --- Individuals ---

export function useIndividuals(companyId?: string) {
  const params = companyId ? `?company_id=${companyId}` : "";
  return useQuery<CRMIndividual[]>({
    queryKey: ["crm-individuals", companyId],
    queryFn: async () => {
      const res = await apiClient.get<{ individuals: CRMIndividual[] }>(
        `/crm/individuals${params}`,
      );
      return res.data.individuals;
    },
  });
}

export function useCreateIndividual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<CRMIndividual>("/crm/individuals", data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-individuals"] }),
  });
}

export function useIndividual(id: string | undefined) {
  return useQuery<CRMIndividual>({
    queryKey: ["crm-individual", id],
    queryFn: async () => {
      const res = await apiClient.get<CRMIndividual>(`/crm/individuals/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useUpdateIndividual(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put<CRMIndividual>(`/crm/individuals/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-individuals"] });
      qc.invalidateQueries({ queryKey: ["crm-individual", id] });
    },
  });
}

// --- Opportunities ---

export function useOpportunities(companyId?: string) {
  const params = companyId ? `?company_id=${companyId}` : "";
  return useQuery<CRMOpportunity[]>({
    queryKey: ["crm-opportunities", companyId],
    queryFn: async () => {
      const res = await apiClient.get<CRMOpportunity[]>(`/crm/opportunities${params}`);
      return res.data;
    },
  });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<CRMOpportunity>("/crm/opportunities", data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-opportunities"] });
      qc.invalidateQueries({ queryKey: ["crm-pipeline"] });
    },
  });
}

export function useUpdateOpportunity(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put<CRMOpportunity>(`/crm/opportunities/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-opportunities"] });
      qc.invalidateQueries({ queryKey: ["crm-pipeline"] });
    },
  });
}

// --- Communications ---

export function useCommunications(filters?: { company_id?: string; individual_id?: string }) {
  const params = new URLSearchParams();
  if (filters?.company_id) params.set("company_id", filters.company_id);
  if (filters?.individual_id) params.set("individual_id", filters.individual_id);
  return useQuery<CRMCommunication[]>({
    queryKey: ["crm-communications", filters],
    queryFn: async () => {
      const res = await apiClient.get<{ communications: CRMCommunication[] }>(
        `/crm/communications?${params.toString()}`,
      );
      return res.data.communications;
    },
  });
}

export function useCreateCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post<CRMCommunication>("/crm/communications", data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-communications"] }),
  });
}

export function useUpdateCommunication(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put<CRMCommunication>(`/crm/communications/${id}`, data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-communications"] }),
  });
}

export function useDeleteCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/crm/communications/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-communications"] }),
  });
}

// --- Pipeline ---

export interface PipelineItem {
  entity_type: "opportunity" | "company_lead";
  id: string;
  title: string;
  company_name: string;
  company_id: string;
  quote_value: number | null;
  engagement_type: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  industry?: string | null;
}

export function usePipeline() {
  return useQuery<Record<string, PipelineItem[]>>({
    queryKey: ["crm-pipeline"],
    queryFn: async () => {
      const res = await apiClient.get<{ pipeline: Record<string, PipelineItem[]> }>(
        "/crm/pipeline",
      );
      return res.data.pipeline;
    },
  });
}

// --- Pipeline Value ---

export function usePipelineValue() {
  return useQuery<{
    stages: Record<string, { count: number; total_value: number }>;
    grand_total: number;
  }>({
    queryKey: ["crm-pipeline-value"],
    queryFn: async () => {
      const res = await apiClient.get("/crm/pipeline/value");
      return res.data;
    },
  });
}
